
<template>
  <div
    :id="identifier"
    ref="mapContainer"
    class="mapElement"
    :class="{ 'custom-geolocate-style': isRecordGeometryEnabled }"
  >
    <div
      class="absolute top-1 w-full flex justify-between items-center pointer-events-none top-slot"
      ref="topControl"
      :class="[buttonGroupStyles, { 'is-mobile': isMobile }]"
    >
      <div ref="scaleBarSlot" class="scalebar-container"/>
      <div class="filters-container">
        <slot :name="Positions.TOP" />
      </div>
    </div>
    <GeoLayerToggle
      v-if="showGeolayerToggle"
      :map="map"
      :is-preload-visible="identifier === 'propertyMap'"
      :map-style="downloadedStyle"
    />
    <FocusController
      v-if="showFocusController"
      :map="map"
      :settings="props.mapSettings"
    />
    <ion-button
      v-if="identifier === 'surveyMap'"
      class="left-button maplibre-like-button open-list-button"
      :class="[{ visible: isGeodataVisible }, { active: geoDataListActive }]"
      size="small"
      :title="$t('appmap.geoDataList')"
      @click="toggleList"
    >
      <ion-icon
        slot="icon-only"
        :icon="listOutline"
      />
    </ion-button>

    <ion-button
      v-if="isMeasuringEnabled"
      class="left-button maplibre-like-button measure-distance-button"
      :class="[
        { visible: isGeodataVisible },
        { active: geoDataListActive },
        { 'objectlocation-mode': identifier === 'objectLocation' }
      ]"
      size="small"
      :title="$t('appmap.measureDistance')"
      @click="startDistanceMeasure()"
    >
      <ion-icon
        slot="icon-only"
        :icon="resizeOutline"
        class="rotated-measuring-icon"
      />
    </ion-button>
   
      <div
        :class="[
          'calculation-box',
          { visible: isCalculationBoxVisible },
          { 'calculation-box-propertymap-mobile': identifier === 'propertyMap' && isMobile }
        ]"
      >
        <div
          id="info"
          v-html="geodataInfo"
        />
      </div>
    <div
      v-if="isGeolocateControlActive && currentAccuracy"
      class="accuracy-box"
    >
      <p><b>{{ t("appmap.accuracy") }}:</b> {{ currentAccuracy }} m</p>
    </div>
    <ion-button
      v-if="identifier === 'propertyMap'"
      :title="$t('appmap.zoomtoall')"
      class="zoomtoall-button"
      size="small"
      color="light"
      @click="emit('map:zoomOverview')"
    >
      <ion-icon
        slot="icon-only"
        :icon="scanOutline"
      />
    </ion-button>
    <ion-button
      v-if="isRotateEnabled"
      class="maplibre-like-button rotate-button right-button"
      size="small"
      @click="toggleRotateMode"
    >
      <ion-icon
        slot="icon-only"
        :icon="reloadOutline"
      />
    </ion-button>
    <ion-button
      v-if="isRecordGeometryEnabled"
      class="maplibre-like-button right-button record-button"
      :class="{ 'record-button-active': isRecordGeometryOpen }"
      size="small"
      color="light"
      @click="toggleRecordGeometryDialog"
    >
      <ion-icon
        slot="icon-only"
        :icon="radioButtonOnOutline"
      />
    </ion-button>
    <ion-button
      v-if="isPredefinedGeomsEnabled"
      class="maplibre-like-button predef-geometry-button right-button"
      size="small"
      color="light"
      @click="openGeometryModal(user?.organisation?.geometryConfig, map?.getCenter())"
    >
      <ion-icon
        slot="icon-only"
        :icon="shapesOutline"
      />
    </ion-button>
    <RecordGeometry
      v-if="isRecordGeometryEnabled && currentPosition && isRecordGeometryOpen && ba"
      :current-position="currentPosition"
      @recorded-geometry="handleRecordedGeometry"
      @delete-geometry="handleDeleteGeometry"
    />
    <div
      v-if="showConfirmAndCancelButtons"
      class="custom-action-buttons"
    >
      <ion-icon
        slot="icon-only"
        class="draw-handler confirm-cancel-handler draw-left-button cancel-draw-button"
        :icon="closeCircleOutline"
        @click="onCancelDrawing"
      />
      <ion-icon
        slot="icon-only"
        class="draw-handler confirm-cancel-handler draw-right-button confirm-draw-button"
        :class="{ disabled: confirmDrawButtonDisabled }"
        :icon="checkmarkCircleOutline"
        @click="confirmDrawingAndHideCalcBox()"
      />
    </div>
    <div
      v-else-if="identifier === 'surveyMap'"
      class="custom-action-buttons"
    >
      <div
        v-if="!!selectedFeature && userCanEditAnmerkung"
        class="circle-wrapper draw-handler draw-left-button edit-draw-button"
        @click="onStartEditing(selectedFeature)"
      >
        <ion-icon
          slot="icon-only"
          class="edit-handler"
          :icon="analyticsOutline"
        />
      </div>
      <div
        v-if="!!selectedFeature"
        class="circle-wrapper draw-handler draw-right-button edit-anmerkung-button"
        @click="editAnmerkungAndHideCalcBox()"
      >
        <ion-icon
          slot="icon-only"
          class="edit-handler"
          :icon="userCanEditAnmerkung ? createOutline : eyeOutline"
        />
      </div>
    </div>
    <slot />
  </div>
</template>

<script
    setup
    lang="ts"
>
import { IonButton, IonIcon } from "@ionic/vue";

import { analyticsOutline, checkmarkCircleOutline, closeCircleOutline, createOutline, eyeOutline, listOutline, radioButtonOnOutline, reloadOutline, scanOutline, shapesOutline, resizeOutline } from 'ionicons/icons';
import { PropType, Ref, computed, onMounted, onUnmounted, ref, watch, nextTick, useSlots } from "vue";
//Interfaces & Enums
import { MapSettingsInterface } from "@/types/map/interfaces";
//Components
import FocusController from "@/components/hzba/Base/FocusController.vue";
//MapBox
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import maplibregl, { GeolocateControl, Map, NavigationControl, Popup } from "maplibre-gl";
//Other
import GeoLayerToggle from "@/components/hzba/Base/GeoLayerToggle.vue";
import useOnceDraw from "@/composables/Map/useDraw";
import { useStore } from "@/composables/useTypedStore";
import useUser from "@/composables/useUser";
import Bestandsaufnahme from "@/models/ba/Bestandsaufnahme";
import { Fragenblock } from "@/models/ba/Fragenblock";
import { createPopup } from "@/utilities/map-helper";
import { Monitoring } from "@/utilities/monitoring";
import { visibilityWatcher, watchElementDimensions } from "@/utilities/observer-utilities";
import { validateCenterSettingMonitored } from "@/utilities/validate-map-settings";
import * as tilebelt from '@mapbox/tilebelt';
import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder';
import '@maplibre/maplibre-gl-geocoder/dist/maplibre-gl-geocoder.css';
import * as turf from "@turf/turf";
import PaintMode from "mapbox-gl-draw-paint-mode";
import RotateMode from 'mapbox-gl-draw-rotate-mode';
import { useI18n } from 'vue-i18n';
import RecordGeometry from "../Map/RecordGeometry.vue";
import { Positions } from "./types";
//Props
const props = defineProps({
    identifier: {
        type: String,
        required: true
    },
    mapSettings: {
        type: Object as PropType<MapSettingsInterface>,
        required: false,
        default: null
    },
    inputJson: {
        type: Object,
        default: undefined
    },
    isRecordGeometryEnabled: {
        type: Boolean,
        default: false
    },
    readonly: {
        type: Boolean,
        default: false
    },
    allowOnlyOneDrawing: {
        type: Boolean,
        required: false
    },
    propertyLocation: {
        type: Object as PropType<{ lon: number, lat: number }>,
        required: false
    },
    ba: {
        type: Object as PropType<Bestandsaufnahme>,
        required: false,
    },
    routeName: {
        type: String,
        required: false
    },
    updateFeaturesCounter: {
        type: Number,
        default: 0,
        required: false
    },
    geoDataListActive: {
      type: Boolean,
      default: false,
      required: false
    },
    isMobile: {
        type: Boolean,
        default: false,
        required: false
    }
});

//Emits
const emit = defineEmits([
    "map:loaded",
    "map:storeGeodata",
    "map:deleteGeodata",
    "map:zoomOverview",
    "update:inputJson",
    "update:mapSearch",
    "update:clearMapSearchInputs",
    "create:point",
    "point:click",
    "list:open",
    "list:close",
    "list:open-item",
    "drawing:created",
    "drawing:updated",
    "drag:start",
    "drag:end"
]);

//Variables
const { t } = useI18n({ useScope: 'global' })
const map = ref<Map>();
const mapContainer = ref();
const isMapSet = ref(false)
let draw: MapboxDraw;
let drawOptions: any;
//scalebar vars
const scaleBarSlot = ref();

const topControl = ref();
const buttonGroupStyles = "scalebar-container";

const geodataInfo = ref("");
const isGeodataVisible = ref(false);
const isCalculationBoxVisible = ref(false);
const selectedFeature = ref(null);
const selectedGeodataInfo = ref(null);
const existingFeaturesLoaded = ref(false);

const isMeasuring = ref(false); //when enabled all drawn geometry isn't saved
const calcboxTimeout = 10000; //time for the calcbox visibility after measuring im ms
let mapOptions: any;
const isLoading = ref(false);
const loadingProgress = ref(0);

const isGeolocateControlActive = ref(false);
const isTrackUserLocationActive = ref(false); // icon: blue circle with dot
const currentAccuracy = ref("");
const currentZoom = ref(0);
let db: any;

const store = useStore();
const isOnline = computed(() => {
    return store.getters["app/isOnline"];
});
const { user } = useUser();
const featureFlags = computed(() => user.value?.organisation?.featureFlags);
const isPredefinedGeomsEnabled = computed(() => props.identifier === "surveyMap" && featureFlags.value.predefinedGeometries && user?.value?.organisation?.geometryConfig);
const isRotateEnabled = ref(false);
const isMeasuringEnabled = computed(() => props.mapSettings?.controller?.measure ?? false);
const downloadedStyle: Ref<any> = ref(null);
let touchStartPosition = {
    x: null,
    y: null
};
let touchEndPosition = {
    x: null,
    y: null
};



const defaultButtonGroupHeight = ref(0);
const currentPosition: any = ref();
const defaultMapZoom = 17;
let popUp: Popup | null = null;
const geolocator: Ref<GeolocateControl | undefined > = ref();

const showGeolayerToggle = computed(() => {
  return !!props.mapSettings?.controller?.layerToggleMenu && !!isMapSet.value && !!downloadedStyle.value;
});

const showFocusController = computed(() => {
  return !!isMapSet.value && !!props.mapSettings?.controller?.focus;
});

const {
    setDrawInstance,
    onClickDraw,
    onDrawModeChange,
    onDrawCreate,
    showConfirmAndCancelButtons,
    confirmDrawButtonDisabled,
    onCancelDrawing,
    onConfirmDrawing,
    onSelectionChange,
    onStartEditing,
    setDrawCreateCallback,
    setDrawEditCallback,
    setSelectionChangeCallback,
    calculateOffsetAfterDraw,
    getExtendedDrawControl,
    collectControlElements,
    setControlVisibility,
    openGeometryModal,
    toggleRotateMode
} = useOnceDraw(props.identifier);

//Methods

// There is no exposed toggle API from maplibre, so using the internal method from here:
// https://github.com/maplibre/maplibre-gl-js/blob/87486a5ef2085e600e8fa4e31252629dd8488dcd/src/ui/control/attribution_control.ts#L107
const collapseAttributionControl = () => {
    const attributionElement = mapContainer.value.querySelector(".maplibregl-ctrl.maplibregl-ctrl-attrib");
    if (attributionElement) {
        attributionElement.removeAttribute('open');
        attributionElement.classList.remove('maplibregl-compact-show');
    }
}

function onClick(e: any) {
    if (onClickDraw(e)) {
        return;
    }
    emit('point:click', e);
    for (const feature of map.value.queryRenderedFeatures(e.point)) {
        if (feature.layer.source === "mapbox-gl-draw-cold") {
            return;
        }
    }

    if (popUp) {
        popUp.remove();
        popUp = null;
    }
    const propertyNamesToInclude = downloadedStyle.value?.on_click_info;

    if (propertyNamesToInclude) {
        const layerIDsToConsider = Object.keys(propertyNamesToInclude);
        const features = map.value.queryRenderedFeatures(e.point, {
            layers: layerIDsToConsider,
        });
        if (features.length > 0) {
            popUp = createPopup(features, propertyNamesToInclude, e.lngLat)
            if (popUp) {
                popUp.addTo(map.value);
            }
        }
    }
}

const getRelatedFragenblock = (feature: any) => {
    const parentFragenblock = props.ba?.findSectionByMapGeometryConfig() as Fragenblock
    return parentFragenblock?.fragenblocks.find((item: any) => item.geoJson?.id === feature?.id) as Fragenblock
}

const currentUserOwnsFragenblock = (feature: any) => {
    const relatedFragenblock = getRelatedFragenblock(feature)
    return !!user.value?.id && relatedFragenblock?.config?.userId === user.value?.id
}

const userCanEditAnmerkung = computed(() => {
    return currentUserOwnsFragenblock(selectedFeature.value);
});

function preloadTiles() {
    // preload project bbox
    //if(navigator.onLine){
    if (isOnline.value) {
        isLoading.value = true;
        precacheRun(map.value, mapOptions);
    }
    // TODO: else show info message to user: sorry - you are offline
}

/**
 * Function to strip off the url of its protocol and search queries to use as a IndexedDB store keyPath
 * @param url
 */
function cleanUrl(url: any) {
    return url.includes("?") ? url.slice(url.indexOf("//") + 2, url.indexOf("?")) : url.slice(url.indexOf("//") + 2);
}

// Gets the needed information related to the Map object
const getContext = function (myMap: any, minZoom: any) {
    Monitoring.consoleOnly("preload context: creating..");
    try {
        const _dimensions = [myMap.getCanvas().width, myMap.getCanvas().height];
        const _tilesize = myMap.transform.tileSize;
        const sc = myMap.getCenter();
        const zmin = Math.min(myMap.getZoom(), minZoom);

        // Only the tiled sources are needed
        const srces = Object.entries(myMap.getStyle().sources);
        const filtered = srces.filter(s => ['vector', 'raster'].indexOf(s[1].type) > -1 && (s[1].url !== undefined || s[1].tiles !== undefined));
        //const _sources = filtered.map(s => myMap.getSource(s[0]).tiles[0]);

        const mySources = [];
        for (let x = 0; x < filtered.length; x++) {
            const curr = filtered[x];
            const mySource = myMap.getSource(curr[0]);
            if (mySource.tiles) {
                const myTiles = mySource.tiles[0];
                mySources.push(myTiles);
            } else {
                Monitoring.consoleOnly("preload context: no tiles for: " + mySource.id);
            }
        }
        // Monitoring.consoleOnly("preload context: all sources: ", mySources);
        return {
            sources: mySources,
            dimensions: _dimensions,
            tilesize: _tilesize,
            startCenter: [sc.lng, sc.lat],
            startZoom: myMap.getZoom(),
            zmin: zmin,
            maxBounds: props.mapSettings?.maxBounds
        };
    } catch (e) {
        Monitoring.consoleOnly("preload context error: ", e);
    }
};

// build and manage the preloader worker
const precacheRun = function (myMap: any, mapOptions: any) {

    const o = Object.assign({}, mapOptions, getContext(myMap, mapOptions.minZoom));
    try {
        Monitoring.consoleOnly("preload run start ");
        /*if (window === self && myMap.precache_worker == undefined) {
            // the actual absolute path of the running script
            // as the module-typed workers are only supported by Chrome
            // we can get the path by throwing an error
            const _imported = ErrorStackParser.parse(new Error('not an actual error!'))[0].fileName;
            Monitoring.consoleOnly("preload run error parsed ");
            // build inline worker
            const target = `
            importScripts('${_imported}');
            let controller;
            let signal;
            onmessage = function (o){
                if (controller !== undefined && controller.signal !== undefined && !controller.signal.aborted){
                    controller.abort();
                }
                if (o.data.abort){
                    postMessage({t: Date.now(), e: true});
                    return;
                }
                controller = new AbortController();
                signal = controller.signal;
                let _func = ${precache_function.toString()};
                Monitoring.consoleOnly("preload run calling ");
                _func.apply(null, [o.data, signal]);
            }`;
            const mission = URL.createObjectURL(new Blob([target], { 'type': 'text/javascript' }));
            myMap.precache_worker = new Worker(mission);
            myMap.precache_worker.onmessage = (e:any) => {
                myMap.precache_worker.time1 = e.data.t;
                Monitoring.consoleOnly(`Preload run time: ${myMap.precache_worker.time1 - myMap.precache_worker.time0}ms`);
            };
            Monitoring.consoleOnly("preload run created ");
        }
        // Some debugging info
        delete myMap.precache_worker.time1;
        myMap.precache_worker.time0 = Date.now();
        //myMap.precache_worker.postMessage(o);
        Monitoring.consoleOnly("preload run end ");
        */

        precache_function(o, null);
    } catch (e) {
        Monitoring.consoleOnly("preload run error: ", e);
    }
};

const precache_function = (o: any, signal: any) => {

    // mapoptions:   container, style, center, minZoom, maxZoom, zoom
    // + context:    sources, dimensions, tilesize, startCenter, startZoom, zmin, maxBounds

    // Final scenario bbox
    //const finalbbox = bounds(o.center, o.zoom, o.dimensions, o.tilesize);
    // transition bbox

    // temporary use smaller bbox for Schwörstadt     TODO: reset to full bbox from mapconfig
    o.maxBounds = [
        [
            7.8398,
            47.6032
        ],
        [
            7.87,
            47.5847
        ]
    ];

    const transbbox = [
        Math.min(o.maxBounds[0][0], o.maxBounds[1][0]),
        Math.min(o.maxBounds[0][1], o.maxBounds[1][1]),
        Math.max(o.maxBounds[0][0], o.maxBounds[1][0]),
        Math.max(o.maxBounds[0][1], o.maxBounds[1][1]),
    ];

    // Monitoring.consoleOnly("preload precache bbox: ", transbbox);

    // all the tiles in a bounding box for a given zoom level
    // including a buffer of 1 tile
    const bboxtiles = (bbox: any, zoom: any) => {
        const sw = tilebelt.pointToTile(bbox[0], bbox[1], zoom);
        const ne = tilebelt.pointToTile(bbox[2], bbox[3], zoom);
        const result = [];
        for (let x = sw[0] - 1; x < ne[0] + 2; x++) {
            for (let y = ne[1] - 1; y < sw[1] + 2; y++) {
                result.push([x, y, zoom]);
            }
        }
        return result;
    };

    let tz: number;
    let tiles = [];

    // Build the tiles pyramid for final scenario
    //for (let z = o.maxZoom; z >= o.minZoom; z--) { // mapconfig max/min zoom
    for (let z = 14; z >= 10; z--) { // temporary use less zoom-levels, TODO: check json for each source and use its minzoom / maxzoom (else many 404-errors for tiles!)
        try {
            const tt = bboxtiles(transbbox, z);
            tiles.push(...tt);
            tz = tt.length;
        } catch (e) {
            Monitoring.consoleOnly("preload precache error collecting tiles: ", e);
        }
    }

    // Monitoring.consoleOnly("preload precache tiles collected: ", tiles);
    // Get the tiles for the transition pan
    //tiles.push(...bboxtiles(transbbox, o.minZoom));

    // Remove duplicates
    tiles = [...new Set(tiles)];
    // From tiles [x,y,z] to URLs
    const urls = tiles.map(t => {
        return o.sources.map((s: any) => {
            return s.replace('{x}', t[0])
                .replace('{y}', t[1])
                .replace('{z}', t[2]);
        });
    }).flat();

    // Monitoring.consoleOnly("preload precache prepared urls: ", urls);
    loadingProgress.value = 0;

    let cnt = 0;
    // Fetch all
    Promise.all(
        urls.map(
            u => {
                // TODO: check if tile already exists locally, decide if reload is required!
                fetch(u, { signal })
                    .then((response) => {
                        cnt++;
                        loadingProgress.value = (cnt / urls.length);
                        if (cnt % 100 === 0) {
                            // Monitoring.consoleOnly("preload precache: downloaded tiles: " + cnt + " / " + urls.length);
                        }
                        if (cnt === urls.length) {
                            isLoading.value = false;
                        }
                        if (!response.ok) {
                            // Monitoring.consoleOnly("preload response not ok: " + u, response);
                            return null;
                        }
                        return u.endsWith("json") ? response.json() : response.arrayBuffer();
                    })
                    .then((data) => {
                        if (data && db) {
                            // add: requires that no object already be in the database with the same key
                            // put: modify an existing entry, or you don't care if one exists already
                            db.transaction(["offline_map"], "readwrite").objectStore("offline_map").put({
                                url: cleanUrl(u),
                                fullurl: u,
                                type: u.endsWith("json") ? "json" : "arrayBuffer",
                                data,
                            });
                        }
                    })
                    .catch(function (error) {
                        cnt++;
                        loadingProgress.value = (cnt / urls.length);
                        if (cnt % 100 === 0) {
                            // Monitoring.consoleOnly("preload precache: downloaded tiles: " + cnt + " / " + urls.length);
                        }
                        if (cnt === urls.length) {
                            isLoading.value = false;
                        }
                        // Monitoring.consoleOnly('preload precache fetch error', error);
                    });
            }
        )
    )
        .then(d => {
            // Monitoring.consoleOnly(`preload precache Estimated gain: ${Math.round(900 * tz / 6)}ms`);
            // Monitoring.consoleOnly(`preload precache Prefetched ${urls.length} tiles at zoom levels [${o.minZoom} - ${o.maxZoom}]`);
            //postMessage({ t: Date.now(), e: false });
        })
        .catch(e => {
            Monitoring.consoleOnly('🔴 Preload precache promise all error');
        });
};

async function fetchFullStyleObject() {
    try {
        if(!mapOptions.style) {
            // should not come this far when no map style is set
            // but adding this check to be sure because MS-866 issue was prevalent for a while
            return;
        }
        Monitoring.setContext("MapStyle", { "StyleURL": mapOptions.style }, true);
        const resp = await fetch(mapOptions.style);

        if (!resp.ok) {
            Monitoring.error("Failed fetchFullStyleObject request.");
        }

        const styleJson = await resp.json();

        downloadedStyle.value = styleJson;
    } catch (error: any) {
        Monitoring.chainError("fetchFullStyleObject error", error);
    } finally {
        Monitoring.clearContexts();
    }
}

function showGeoDataListButton(features: any) {
    isGeodataVisible.value = features.length > 0;
}

//functions for calculation box data and visibility
function confirmDrawingAndHideCalcBox() {
  onConfirmDrawing();
  isCalculationBoxVisible.value = false;
}
function editAnmerkungAndHideCalcBox(){
    emit('list:open-item', getRelatedFragenblock(selectedFeature)); 
    isCalculationBoxVisible.value = false;
}
function showGeodata(features: any[]) {
  for (let i = 0; i < features.length; i++) {
    features[i].properties.calc = calcGeo(features[i]);
    features[i].properties.title = features[i].properties.calc.label;
  }
  let geodataText = "";
  features.forEach((element: any) => {
    const title = element.properties.title ? element.properties.title : "-";
    const id = element.id;
    geodataText = `<span class="geodata-entry" data-feature-id="${id}">${title}</span><br>`; 
});
  geodataInfo.value = geodataText;
  isCalculationBoxVisible.value = features.length > 0;
}
function getSelectedGeodataToCalcBox(feature: any) {
    if (!feature) {
        geodataInfo.value = "";
        isCalculationBoxVisible.value = false;
        return;
    }
    feature.properties.calc = calcGeo(feature);
    feature.properties.title = feature.properties.calc.label;
    const title = feature.properties.title ? feature.properties.title : "-";
    const id = feature.id;
    geodataInfo.value = `<span class="geodata-entry" data-feature-id="${id}">${title}</span><br>`;
    isCalculationBoxVisible.value = true;
}
// calulationbox functions end

const removeAllOtherFeatures = (featureId: string) => {
    const drawnFeatures: any = draw.getAll();
    const featuresToDelete = drawnFeatures.features.filter((item: any) => item.id !== featureId);
    featuresToDelete.forEach((item: any) => {
        draw.delete(item.id);
    });
}

function afterDrawCreated() {
    const drawnFeatures: any = draw.getAll();
    emit("update:inputJson", drawnFeatures);

    if (props.mapSettings?.minMax?.max && drawnFeatures.features.length >= props.mapSettings?.minMax?.max) {
        setControlVisibility("none");
    }
    showGeodata(drawnFeatures?.features);
    showGeoDataListButton(drawnFeatures?.features);


    if (props.allowOnlyOneDrawing) {
        const lastFeature = drawnFeatures.features[drawnFeatures.features.length - 1];
        removeAllOtherFeatures(lastFeature.id);
    }

    isCalculationBoxVisible.value = false;
}

function afterDrawDeleted() {
    const featuresAfterDelete: any = draw.getAll();

    emit("update:inputJson", featuresAfterDelete);

    if (
        props.mapSettings?.minMax?.max &&
        featuresAfterDelete.features.length <= props.mapSettings?.minMax?.max
    ) {
        setControlVisibility("block");
    }
    showGeodata(featuresAfterDelete?.features);
    showGeoDataListButton(featuresAfterDelete?.features);

    //Since createPropertyMap can have just one point on the map, this is the trigger to clear input fields when point is removed
    if (props.allowOnlyOneDrawing) {
        emit('update:clearMapSearchInputs');
    }
}

function afterDrawUpdate() {
    const drawnFeatures: any = draw.getAll();

    emit("update:inputJson", drawnFeatures);

    if (props.mapSettings?.minMax?.max && drawnFeatures.features.length >= props.mapSettings?.minMax?.max) {
        setControlVisibility("none");
    }
    showGeodata(drawnFeatures?.features);
    showGeoDataListButton(drawnFeatures?.features);


    // TODO: check if this is really necessary? On update, the inputJson is already emitted
    // if (props.allowOnlyOneDrawing) {
    //     emit('update:clearMapSearchInputs')
    // }

    if (props.createPropertyMap) {
        emit('update:clearMapSearchInputs')
    }
}

async function handleDrawCreateAndUpdateSurvey(feature: any) {
    const drawItem = feature;
    const parentFragenblock = props.ba?.findSectionByMapGeometryConfig() as Fragenblock;
    const userId = user.value?.id;

    const fragenblock = await store.dispatch("fragenStore/createFragenblockFromFeature", { ba: props.ba, parentFragenblock, drawItem, userId });
    
    if (fragenblock) {
        afterDrawCreated();
        emit("drawing:created", fragenblock);
    }
}

async function handleDrawEditedAndUpdateSurvey( editedId:string) {
    const feature = draw.getAll().features.find((item: any) => item.id === editedId);
    const relatedFragenblock = getRelatedFragenblock(feature);

    relatedFragenblock.geoJson = feature;
    const updateFields = { geoJson: feature };
    store.dispatch("fragenStore/updateFragenblock", { ba: props.ba, fragenblock: relatedFragenblock, updateFields });
}

setDrawCreateCallback(handleDrawCreateAndUpdateSurvey);

setDrawEditCallback(handleDrawEditedAndUpdateSurvey);

function emitLoaded() {
    emit("map:loaded", map.value);
    displayPropertyLocation();
    displaySurveyMapData();

    const locateButton = document.getElementsByClassName('maplibregl-ctrl-geolocate');
    if (locateButton.length > 0) {
        locateButton[0].addEventListener("click", function () {
            if (!isGeolocateControlActive.value) {
                isGeolocateControlActive.value = true;
                Monitoring.consoleOnly("Turn Find-my-location On");
            } else if (!isTrackUserLocationActive.value) {
                isGeolocateControlActive.value = false;
                currentAccuracy.value = "";
                Monitoring.consoleOnly("Turn Find-my-location Off");
            }
        });
    }
}

function displayPropertyLocation() {
    if (props.propertyLocation) {
        const pointGeometry = {
            type: 'Point',
            coordinates: [Number(props.propertyLocation.lon), Number(props.propertyLocation.lat)]
        }

        const point = {
            type: 'Feature',
            geometry: pointGeometry,
            properties: {}
        } as any

        draw.add(point)
        const drawnFeatures: any = draw.getAll();
        showGeoDataListButton(drawnFeatures?.features);
    }
}

async function displaySurveyMapData() {
    if (props.identifier !== 'surveyMap') return;
    if (!props.ba) return;

    const parentFragenblock = props.ba?.findSectionByMapGeometryConfig() as Fragenblock
    const features = parentFragenblock?.fragenblocks.filter(item => item.geoJson) ?? [];

    draw.deleteAll();
    for (let i = 0; i < features.length; i++) {
        draw.add(features[i].geoJson)
    }
    existingFeaturesLoaded.value = true;
    const drawnFeatures: any = await draw.getAll();
    showGeoDataListButton(drawnFeatures?.features);
}


const handleSelection = (e: any) => {
  if (e.features && e.features.length > 0) {
    const feature = e.features[0];
    const featureId = feature.id;
    selectedFeature.value = feature;

    if (selectedGeodataInfo.value) {
      selectedGeodataInfo.value.classList.remove("bold-text");
    }

    selectedGeodataInfo.value = document.querySelector(
      `[data-feature-id="${featureId}"]`
    );

    if (selectedGeodataInfo.value) {
      selectedGeodataInfo.value.classList.add("bold-text");
    }
  } else {
    selectedFeature.value = null;
    if (selectedGeodataInfo.value) {
      selectedGeodataInfo.value.classList.remove("bold-text");
    }
  }
}

setSelectionChangeCallback(handleSelection);

function calcGeo(props: any) {
    const { type, coordinates } = props.geometry;
    let val = null;
    let unit = "";

    let lat, lng, latDirection, lngDirection; // Declare the variables outside the switch statement

    switch (type) {
        case "Point":
            lat = Number(coordinates[1]).toFixed(4) as any;
            lng = Number(coordinates[0]).toFixed(4) as any;
            latDirection = lat > 0 ? "N" : "S";
            lngDirection = lng > 0 ? "E" : "W";

            val = `${Math.abs(lat)} ${latDirection}, ${Math.abs(lng)} ${lngDirection}`;
            break;
        case "LineString":
        case "MultiLineString":
            val = Math.round(turf.length(props.geometry, { units: "meters" })).toLocaleString('de-DE');
            unit = "m";
            break;
        case "Polygon":
        case "MultiPolygon":
            val = Math.round(turf.area(props.geometry)).toLocaleString('de-DE');
            unit = "m²";
            break;
    }


    const obj = {
        area: val,
        unit: unit,
        label: val !== null ? val + " " + unit : null,
    };

    return obj;
}

const isRecordGeometryOpen = computed(() => store.state.app.isRecordGeometryOpen);

function addScaleControlToTop() {
  if (!map.value || !props.mapSettings?.controller?.scaleBar) return;

  const scaleControl = new maplibregl.ScaleControl({
    maxWidth: 85,
    unit: 'metric',
  });

  const scaleEl = scaleControl.onAdd(map.value); // returns HTMLElement
  scaleEl.classList.add('scalebar-container');

  if (scaleEl && scaleBarSlot.value) {
    scaleEl.classList.add('pointer-events-auto');
    scaleBarSlot.value.appendChild(scaleEl);
  }
}

function toggleRecordGeometryDialog(): void {
    if ( !isRecordGeometryOpen.value && !isTrackUserLocationActive.value ) {
        geolocator.value?.trigger();
        isGeolocateControlActive.value = true;
    }
    onCancelDrawing();
    store.dispatch("app/setRecordGeometryOpen", !isRecordGeometryOpen.value);
}

async function handleRecordedGeometry( event: { isFinal: boolean, feature: any }) {
    if (draw.get( event.feature.id )) {
        draw.delete( event.feature.id );
    }

    draw.add(event.feature);

    if ( event.isFinal ) {
        await handleDrawCreateAndUpdateSurvey(event.feature);
        store.dispatch("app/setRecordGeometryOpen", false);
    }
}

function handleDeleteGeometry(id: string) {
   const deleteResult = draw.delete(id);
//    Monitoring.consoleOnly("deleteResult", deleteResult);
}
function startDistanceMeasure() {
    isMeasuring.value = true;
    draw.changeMode('draw_line_string');
}
function handleMeasuringFeature(event: any) {
    const feature = event.features[0];
    if (feature.geometry.type === 'LineString') {
        const length = Math.round(turf.length(feature, { units: "meters" }));
        geodataInfo.value = `${length.toLocaleString()} m`;
        isCalculationBoxVisible.value = true;
        draw.delete(feature.id);
    }
    isMeasuring.value = false;

    setTimeout(() => {
        isCalculationBoxVisible.value = false;
        geodataInfo.value = "";
    }, calcboxTimeout);
}
const toggleList = () => {
    if(!props.geoDataListActive) {
        emit('list:open');
    } else {
        emit('list:close');
    }
}

watch(isRecordGeometryOpen, (newVal, oldVal) => {
    const geolocateButton: any = document.querySelector(".maplibregl-ctrl-geolocate");
    if ( geolocator.value && geolocateButton) {
        if ( newVal ) {
            geolocateButton.style.pointerEvents = "none";
        } else {
            geolocateButton.style.pointerEvents = "auto";
            isGeolocateControlActive.value = false;
        }
    }
})

watch(() => props.ba, (newVal) => {
    if (newVal && existingFeaturesLoaded.value === false) {
        displaySurveyMapData();
    }
});


// workaround, because changes in Fragenblock don't propagate to BA
watch(() => props.updateFeaturesCounter, () => {
    displaySurveyMapData();
    handleSelection({});
});


//updates the calculation box when change occurs
watch(selectedFeature, (val) => {
  getSelectedGeodataToCalcBox(val);
});

watch([mapContainer, defaultButtonGroupHeight, showFocusController, showGeolayerToggle], ([container, height, ...buttons]) => {
    let listButtonOffset = height + 5;
    if (!container) {
        return;
    }
    buttons.forEach((button) => {
        if (button) {
          listButtonOffset += 40;
        }
    });
    container?.style?.setProperty("--open-list-button-top-offset", `${listButtonOffset}px`);
});
//bugfix: propertyLocation is undefined or unavailable when component is first loaded, so it needs to be updated when its available
let unsubscribePropertyLocationWatcher: (() => void) | null = null;

unsubscribePropertyLocationWatcher = watch(
  () => props.propertyLocation,
  (newLoc, oldLoc) => {
    if (
      map.value &&
      newLoc &&
      (!oldLoc ||
        newLoc.lon !== oldLoc.lon ||
        newLoc.lat !== oldLoc.lat)
    ) {
      map.value.jumpTo({
        center: [Number(newLoc.lon), Number(newLoc.lat)],
        zoom: map.value.getZoom() || 17,
      });
      unsubscribePropertyLocationWatcher?.();
    }
  },
  { immediate: true }
);

//Lifecycle Hooks
onMounted(() => {
    const dbOpen = indexedDB.open("ms_store", 3);
    dbOpen.onupgradeneeded = (e) => {
        e.target.result.createObjectStore("offline_map", { keyPath: "url" });
    };

    dbOpen.onerror = (e: any) => {
        Monitoring.withScope((scope) => {
            scope.setContext("AppMap", { identifier: props.identifier });
            Monitoring.chainError("IndexedDB error in AppMap", e);
        });
    }

    dbOpen.onsuccess = (e) => {

        db = e.target.result;

        if (featureFlags.value?.offlineMap) {
            // Custom load resource function to save external resources into IndexedDB store
            maplibregl.addProtocol("ms_store_add", (params) => {
                params.url = params.url.replace("ms_store_add", "https");
                // Monitoring.consoleOnly("ms_store_add: ", params);
                // TODO: check if tile already exists locally, decide if reload is required!
                return new Promise((resolve, reject) => {
                    fetch(params.url)
                        .then((response) => {
                            if (!response.ok) {
                                // Monitoring.consoleOnly("ms_store_add response not ok: " + params.url, response);
                                return null;
                            }
                            return params.type === "json" ? response.json() : response.arrayBuffer();
                        })
                        .then((data) => {
                            if (data && db) {
                                // add: requires that no object already be in the database with the same key
                                // put: modify an existing entry, or you don't care if one exists already
                                db.transaction(["offline_map"], "readwrite").objectStore("offline_map").put({
                                    url: cleanUrl(params.url),
                                    fullurl: params.url,
                                    type: params.type,
                                    data,
                                });
                                params.data = data;
                                resolve(params); // promise was successful -> call resolve method from function params
                            }
                        })
                        .catch(function (error) {
                            Monitoring.consoleOnly('ms_store_add then mserror', error);
                        });
                });
            });

            // Custom load resource function to fetch external resources from IndexedDB store instead
            maplibregl.addProtocol("ms_store_get", (params, abortController) => {
                params.url = params.url.replace("ms_store_get", "https");
                Monitoring.consoleOnly("ms_store_get: ", params);
                return new Promise((resolve, reject) => {
                    const dbTransaction = db.transaction("offline_map").objectStore("offline_map").get(cleanUrl(params.url));
                    dbTransaction.onsuccess = (e: any) => {
                        //Monitoring.consoleOnly("ms_store_get success: ", params, " result: ", e);
                        if (e.target.result) {
                            delete e.target.result.url;
                            resolve(e.target.result);
                        } else {
                            abortController.abort();
                        }
                    };
                    dbTransaction.onerror = (e: any) => {
                        // Handle errors!
                        Monitoring.consoleOnly("ms_store_get error", e);
                    };
                });
            });
        }
    };

    mapOptions = {
        container: mapContainer.value,
        style: props.mapSettings?.style || props.mapSettings?.mapStyle?.style,
        center: props.identifier === "surveyMap" && props.propertyLocation ?
            [Number(props.propertyLocation.lon), Number(props.propertyLocation.lat)] :
            validateCenterSettingMonitored(props.mapSettings),
        minZoom: props.mapSettings?.minZoom || props.mapSettings?.mapStyle?.minZoom,
        maxZoom: props.mapSettings?.maxZoom || props.mapSettings?.mapStyle?.maxZoom,
        zoom: props.mapSettings?.mapInitialZoomLevel || props.mapSettings?.mapStyle?.mapInitialZoomLevel || defaultMapZoom
    };
    fetchFullStyleObject();

    // Change URL protocol to custom
    // alle externen requests abfangen und das protokoll ändern
    if (featureFlags.value?.offlineMap) {
        mapOptions.transformRequest = (url: any, resourceType: any) => {
            // do not change url protocol of first loading style-document
            if (url.includes("static.maptoolkit.net/styles/movinglayers/") || isOnline.value) {
                return { url: url };
            }
            return { url: url.replace(/^[a-z]+:\/\//, "ms_store_get://") };
            //return { url: url.replace(/^[a-z]+:\/\//, isOnline.value ? "ms_store_add://" : "ms_store_get://") };
            //return { url: url.replace(/^[a-z]+:\/\//, navigator.onLine ? "ms_store_add://" : "ms_store_get://") };
        };
    }

    // https://maplibre.org/maplibre-gl-js/docs/examples/mapbox-gl-draw/
    MapboxDraw.constants.classes.CONTROL_BASE = 'maplibregl-ctrl';
    MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-';
    MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group';

    // Drawbox overlay setup
    drawOptions = props.mapSettings?.drawOptions || {};
    if (props.identifier === "surveyMap" && props.mapSettings?.styles) {
        // styles object is sometimes differently located depending on project and the map-identifier
        drawOptions.styles = props.mapSettings?.styles;
    }
    drawOptions.modes = Object.assign({}, MapboxDraw.modes, { draw_paint_mode: PaintMode, static: StaticMode, RotateMode });
    isRotateEnabled.value = drawOptions?.controls?.rotate ?? false;
    const offset = calculateOffsetAfterDraw(drawOptions);
    if(offset) {
        let position = 0;

        //helper function to calculate the offset for the buttons
        // if AppMap is refactored to use addControl this could make this function obsolete
        // e.g. mapLibre.value.addControl(new CustomControl(topRightControl.value), Positions.TOP_RIGHT);
        const getOffset = () => {
            const newOffset = offset + position * 39;
            position += 1;
            return newOffset;
        }

        if(isRotateEnabled.value) {
            mapContainer.value.style.setProperty("--rotate-button-top-offset", `${getOffset()}px`);
        }
        if(props.isRecordGeometryEnabled) {
            mapContainer.value.style.setProperty("--record-button-top-offset", `${getOffset()}px`);
        }
        if(isPredefinedGeomsEnabled.value) {
            mapContainer.value.style.setProperty("--predef-geometry-top-offset", `${getOffset()}px`);
        }
    }
    try {
        map.value = new Map(mapOptions);    
    } catch (error: any) {
        Monitoring.withScope((scope, scrub) => {
            scope.setContext("AppMap info", { identifier: props.identifier, style: scrub(mapOptions.style) });
            Monitoring.chainError("Error while initializing map", error);
        });
    }

    if(!map.value) {
        // TODO: show placeholder or reload button
        return;
    }
    draw = new MapboxDraw(drawOptions);
    setDrawInstance(map.value, draw, drawOptions);
    isMapSet.value = true;

    map.value.addControl(new NavigationControl({
        showCompass: props.mapSettings?.controller?.compass,
        showZoom: props.mapSettings?.controller?.zoom
    }), 'top-left');
    geolocator.value = new GeolocateControl({
        positionOptions: {
            enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showAccuracyCircle: true,
        fitBoundsOptions: {
            maxZoom: defaultMapZoom,
        },
    });
    map.value.addControl(geolocator.value, 'top-left');
    geolocator.value.on('trackuserlocationstart', () => {
        isTrackUserLocationActive.value = true;

    });
    geolocator.value.on('trackuserlocationend', () => {
        isTrackUserLocationActive.value = false;
    });
    geolocator.value.on('userlocationlostfocus', () => {
        const geolocateButton: any = document.querySelector(".maplibregl-ctrl-geolocate");
        if ( geolocateButton && geolocateButton.style.pointerEvents === "none") {
            geolocateButton.style.pointerEvents = "auto";
        }
    });
    geolocator.value.on('geolocate', (event) => {

        const { latitude, longitude, altitude } = event.coords;
        currentPosition.value = { lat: latitude, lon: longitude, alt: altitude };
        currentAccuracy.value = Number(Number(event.coords.accuracy).toFixed(2)).toLocaleString('de-DE'); // in meters

        const isActive = geolocator.value?._watchState === "ACTIVE_LOCK" ;
        if (isActive) {
            Monitoring.consoleOnly("geolocate event - active");
            map.value?.jumpTo({
                center: [longitude, latitude],
                zoom: defaultMapZoom,
            });

            if (props.identifier === "surveyMap" && isRecordGeometryOpen.value) {
                const geolocateButton: any = document.querySelector(".maplibregl-ctrl-geolocate");
                geolocateButton.style.pointerEvents = "none";
            }

            // Call trigger to ensure the control remains active
            geolocator.value?.trigger();
            isGeolocateControlActive.value = true;
        }
    });

    if (featureFlags.value.geoCoder && props.createPropertyMap) {
        const geocoder = new MaplibreGeocoder({
            forwardGeocode: async (cfg: any) => {
                const response = await fetch(`https://geocoder.maptoolkit.net/search?q=${cfg.query}&language=${cfg.language[0]}&api_key=movinglayers`)
                const result = await response.json();
                return {
                    features: result.map((e: any) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [e.lon, e.lat]
                        },
                        place_type: ["place"],
                        place_name: e.display_name,
                        properties: e,
                        center: [e.lon, e.lat]
                    }))
                };
            },
        }, {
            showResultsWhileTyping: true,
            showResultMarkers: false,
            marker: false,
            maplibregl: maplibregl
        })
        map.value.addControl(geocoder, 'top-left')

        geocoder.on('result', function (e: any) {
            const localGeometry = {
                type: e.result.geometry.type,
                coordinates: [Number(e.result.geometry.coordinates[0]), Number(e.result.geometry.coordinates[1])]
            }
            const point = {
                type: 'Feature',
                geometry: localGeometry,
                properties: {}
            } as any

            draw.add(point)
            let drawnFeatures: any = draw.getAll();

            if (props.mapSettings?.minMax?.max) {
                if (drawnFeatures.features.length >= props.mapSettings?.minMax?.max) {
                    setControlVisibility("none");
                }

                if (drawnFeatures.features.length > props.mapSettings?.minMax?.max) {
                    draw.delete(drawnFeatures.features[0].id)
                    drawnFeatures = draw.getAll();
                }
            }
            showGeoDataListButton(drawnFeatures?.features);
            emit('update:mapSearch', e.result.properties)
        });
    }

    map.value?.on("error", (e: any) => {
        Monitoring.withScope((scope, scrub) => {
            scope.setContext("AppMap info", { "identifier": props.identifier, "style": scrub(mapOptions.style), "maplibre_version": map.value?.version });
            Monitoring.chainError("MapLibre error", e);
        });
    });
    map.value?.on("load", emitLoaded);
    map.value?.on("zoom", () => currentZoom.value = map.value.getZoom());
    map.value?.on('draw.create', (event: any) => {

        if (isMeasuring.value) {
            handleMeasuringFeature(event);
            return;
        }

        //original draw create logic
        if (props.identifier === "surveyMap")  onDrawCreate();
        else    afterDrawCreated();
    });
    map.value?.on("draw.delete", () => afterDrawDeleted());
    map.value?.on("draw.update", () => afterDrawUpdate());
    map.value?.addControl(getExtendedDrawControl(), "top-right");

    if(props.identifier === "surveyMap") {
        map.value?.on("draw.selectionchange", onSelectionChange);
    }

    if (props.mapSettings?.initialDrawnFeatures) {
        draw.add(props.mapSettings?.initialDrawnFeatures)
    }

    if (props.mapSettings?.maxBounds) {
        map.value?.setMaxBounds(props.mapSettings?.maxBounds);
    }

    map.value.once("load", () => {
        collectControlElements();
        collapseAttributionControl();
        //first time visible
        visibilityWatcher(mapContainer.value, () => {
          watchElementDimensions(mapContainer.value.querySelector(".maplibregl-ctrl-top-left"), (_width, height) => {
            defaultButtonGroupHeight.value = height;
          });
          collapseAttributionControl();
        }, true);
        // every time visible
        visibilityWatcher(mapContainer.value, () => {
            if (!isGeodataVisible.value) {
                // sometimes button was not shown after "load" event, use this as a fallback
                const drawnFeatures: any = draw.getAll();
                showGeoDataListButton(drawnFeatures?.features);
            }
        });

        props.inputJson && draw.add(props.inputJson);
        if (props.readonly) {
            draw.changeMode("static");
        }
        if (
            props.mapSettings?.minMax?.max &&
            draw.getAll().features.length >= props.mapSettings?.minMax?.max
        ) {
            setControlVisibility("none");
        }
        const drawnFeatures: any = draw.getAll();
        showGeoDataListButton(drawnFeatures?.features);
    });

    map.value?.on("draw.modechange", () => {
        onDrawModeChange();
    });

    map.value?.on("dragstart", () => {
       emit("drag:start");
    });

    map.value?.on("dragend", () => {
        emit("drag:end");
    });
    map.value?.on("click", (e: any) => {
        onClick(e)
    });
    map.value?.on('touchstart', (e: any) => {
        touchStartPosition = e.point;
    });

    map.value?.on('touchend', (e: any) => {
        touchEndPosition = e.point;
        if (touchStartPosition.x === touchEndPosition.x &&
            touchStartPosition.y === touchEndPosition.y) {
            onClick(e)
        }
    });
    addScaleControlToTop();



});

onUnmounted(() => {

  map.value?.remove();
});
</script>

<style scoped>
@import "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css";
@import "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.3.0/mapbox-gl-draw.css";
</style>

<style
    scoped
    lang="scss"
>

.mapElement {
  position: relative;

    .calculation-box p {
        margin: 1.25px;
    }

   .calculation-box {
      position: absolute;
      top: 9px;
      left: 55px;
      right: auto;
      bottom: auto;
      border-radius: 4px;
      z-index: 10;
      min-width: 85px;
      max-width: 200px;
      background-color: var(--white100); // Match button color
      color: var(--black100);            // Match button text color
      box-shadow: 0 0 0 2px rgba(0, 0, 0, .1);
      border: none;
      --background: var(--ion-color-light);
      padding: 3.5px;
      text-align: center;
      display: none;
      height: 27.3px;
      width: auto;
      overflow: auto;
      --padding-bottom: 3px;
      --padding-top: 3px;
      --padding-start: 3px;
      --padding-end: 3px;
    }

    .calculation-box.visible {
    display: block;
    }
    .calculation-box-propertymap-mobile {
      top: 45px;
    }
    .accuracy-box p {
        margin: 1.25px;
    }

  .accuracy-box {
    border-radius: 8px;
    z-index: 99;
    min-width: 85px;
    max-width: 200px;
    position: absolute;
    top: 10px;
    left: 55px;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 5px;
    text-align: center;
    max-height: 100px;
    overflow: auto;
  }

  .visible {
    display: block;
  }

  .geodata-entry {
    cursor: pointer;
  }

  :deep(.mapbox-gl-draw_ctrl-draw-btn.active) {
    background-color: var(--secondary, rgba(0, 0, 0, 0.05));
  }
}

.zoomtoall-button {
  position: absolute;
  top: 142px;
  left: 8px;
  z-index: 4;
  width: 30px;
  color: var(--black100);
  background-color: var(--white100);
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, .1);
  --padding-bottom: 3px;
  --padding-top: 3px;
  --padding-start: 3px;
  --padding-end: 3px;
}

.maplibre-like-button {
  @include canvas-clipping;
  position: absolute;
  z-index: 4;
  width: 30px;
  color: var(--black100);
  background-color: var(--white100);
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, .1);
  --padding-bottom: 3px;
  --padding-top: 3px;
  --padding-start: 3px;
  --padding-end: 3px;
  --background-activated: var(--secondary, rgba(0, 0, 0, 0.05));
}

.left-button {
  left: 8px;
}

.right-button {
  right: 8px;
}

.open-list-button {
  top: var(--open-list-button-top-offset, 181px);
  --background: var(--ion-color-light)
}
.measure-distance-button {
  top: var(--measure-distance-button-top-offset, 220px);
  --background: var(--ion-color-light)
}
ion-button.measure-distance-button:hover {
  --background: rgba(0, 0, 0, 0.1); 
}
.measure-distance-button.objectlocation-mode {
  top: var(--measure-distance-button-top-offset, 141px);
}
.record-button {
  top: var(--record-button-top-offset, 220px);
}

.record-button-active::part(native) {
  background: var(--secondary);
}

.predef-geometry-button {
  top: var(--predef-geometry-top-offset, 259px);
}

.rotate-button {
  top: var(--rotate-button-top-offset, 298px);
  --background: var(--ion-color-light)
}

ion-button.rotate-button.active,
ion-button.predef-geometry-button.active,
ion-button.open-list-button.active {
  --background: var(--secondary, rgba(0, 0, 0, 0.05));
}

.circle-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;

  ion-icon {
    padding: 0.3rem;
  }

  &::after {
    content: "";
    position: absolute;
    --ring-padding: 0.3em;
    top: var(--ring-padding);
    left: var(--ring-padding);
    right: var(--ring-padding);
    bottom: var(--ring-padding);
    border: 1px solid currentColor;
    border-radius: 50%;
  }
}

.draw-handler {
  @include canvas-clipping;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  z-index: 10;
  opacity: 0.8;
  color: var(--ion-color-dark-tinte100);
  border-radius: 50%;
  --ionicon-stroke-width: 10px;
}

.confirm-cancel-handler {
  height: 2.5rem;
  width: 2.5rem;
}

.edit-handler {
  height: 1.5rem;
  width: 1.5rem;
}

.draw-handler.draw-left-button {
  right: 6.75rem;
}

.draw-handler.draw-right-button {
  right: 3.5rem;
}

.confirm-draw-button {
  background: var(--secondary);
  background: #DCF4DD;
}

.confirm-draw-button.disabled {
  opacity: 0.3;
}

.cancel-draw-button {
  background: #F2C9CC;
  background: #F4DDDE;
}

.edit-anmerkung-button {
  background: #FFEA17;
}

.edit-draw-button {
  background: #DDEBF4;
}

:deep(.maplibregl-ctrl-group) {
  @include canvas-clipping;
}

:deep(.maplibregl-ctrl.maplibregl-ctrl-attrib) {
  margin-bottom: var(--ion-safe-area-bottom, 0px);
}

.scalebar-container {
  position: absolute;
  top: -8px;
  left: 15px;
  right: 10px;
  padding: 25px;
  z-index: 10;
  display: flex;
  font-size: large;
  align-items: center;
}

.filters-container {
  display: flex;
  top: 10px;
  align-items: center;
  margin-left: 65px;    /* Add left margin to push filters away from the scalebar */
  gap: 8px;             /* Space between filter buttons */
  pointer-events: auto; /* Enable interaction */
}
.scalebar-container .maplibregl-ctrl-scale {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.top-slot.is-mobile {
  flex-direction: column !important;
  align-items: flex-end !important;
  gap: 0;
  left: 0px;
}
.is-mobile .scalebar-container {
  position: absolute !important;
  left: 55px !important;
  top: 55px !important; 
  margin-top: 0 !important;
  margin-left: 0 !important;
  padding: 0 0 0 0;
  width: auto;
  justify-content: flex-start;
}
.is-mobile .filters-container {
  margin-left: 0 !important;
  width: 90%;
  justify-content: flex-end;
}

@media (max-width: 1200px) {

.filters-container {
  display: flex;
  top: 10px;
  align-items: center;
  margin-left: 65px;    
  padding-left: 50px;
  gap: 8px;             
  pointer-events: auto; 
}
}
</style>
<style lang="scss">

/* Custom styles for the GeolocateControl dot */
.custom-geolocate-style .maplibregl-user-location-dot {
    position: relative;             /* Required for positioning pseudo-elements */
    width: 40px;                    /* Circle size */
  height: 40px;
    border: 4px solid black;          /* Circle border */
    border-radius: 50%;             /* Make it a circle */
    background-color: transparent;  /* Transparent background */
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

/* Completely override the default ::before and ::after pseudo-elements */
.custom-geolocate-style .maplibregl-user-location-dot::before,
.custom-geolocate-style .maplibregl-user-location-dot::after {
    /* Completely reset MapLibre's styles */
    all: unset;                     /* Reset all inherited styles */
    content: '';                    /* Ensure pseudo-elements are displayed */
    position: absolute;             /* Position them inside the circle */
  background-color: black;
  width: 40px;
  height: 1px;
}

/* Rotate to form the "X" */
.custom-geolocate-style .maplibregl-user-location-dot::before {
  transform: rotate(45deg);
}

.custom-geolocate-style .maplibregl-user-location-dot::after {
  transform: rotate(-45deg);
}


.custom-geolocate-style .maplibregl-user-location-dot::before,
.custom-geolocate-style .maplibregl-user-location-dot::after {
  z-index: 2;
}

.maplibregl-popup-close-button {
  right: 0.25rem;
  top: 0.25rem;
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  padding-bottom: 0.5rem;
  font-size: 1.5rem;
}

.maplibre-popup-title {
  margin-top: 1rem;
}

.rotated-measuring-icon {
  transform: rotate(45deg); //TO DO: Ask Florian if the rotation or icon is ok
}
</style>
